import React, { memo } from "react";
import { User, X, Check, Badge, Crown, AlertTriangle } from "lucide-react";
import { TbMessageFilled } from "react-icons/tb";
import { motion } from "framer-motion";

import { useState, useEffect } from "react";

export const EnhancedSongCard = memo(
  ({
    item,
    index,
    type,
    isFirst,
    newApprovedItems,
    handleDecline,
    handleAccept,
    handlePlayed,
    handleNotYet,
    isHistory,
  }) => {
    const [elapsedTime, setElapsedTime] = useState(0);
    const isNewItem = newApprovedItems?.[item.id];
    const timerTime = 15 * 60 * 1000; // 15 mins in milliseconds
    const approvedTime = new Date(item.approvedAt).getTime();
    const isOverdue =
      type === "approved" &&
      !isHistory &&
      Date.now() - approvedTime > timerTime &&
      !item.isAnnouncement &&
      !item.isFastTrack;
    const isFastTrack = type === "approved" && item.isFastTrack && !isHistory;
    const shouldAnimate = isOverdue || isFastTrack;

    useEffect(() => {
      let timer;
      if (isOverdue) {
        timer = setInterval(() => {
          setElapsedTime(Date.now() - approvedTime);
        }, 1000);
      }
      return () => clearInterval(timer);
    }, [isOverdue, approvedTime]);

    const formatTimer = (timeInMs) => {
      const minutes = Math.floor(timeInMs / (1000 * 60));
      const hours = Math.floor(minutes / 60);

      if (hours >= 1) {
        return `More than ${hours} hr${hours > 1 ? "s" : ""}`;
      } else {
        return `More than ${minutes} min${minutes > 1 ? "s" : ""}`;
      }
    };

    const formatItemTime = (timestamp) =>
      timestamp
        ? new Date(timestamp).toLocaleTimeString([], {
            hour: "2-digit",
            minute: "2-digit",
            hour12: true,
          })
        : "--:--";

    return (
      <div className={`w-full ${isNewItem ? "bg-green-50/50" : ""}`}>
        <div
          className={`bg-white rounded-xl px-3 py-3 shadow-sm border-2 relative ${
            isOverdue
              ? "border-red-500"
              : isFastTrack
                ? "border-yellow-400"
                : "border-gray-200/50"
          }`}
        >
          {/* Badge and Animation */}
          {isOverdue ? (
            <div className="absolute flex items-end gap-1 top-2 right-3 text-white text-xs font-bold rounded-full shadow-lg z-10">
              <div className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium bg-red-500 text-white">
                <motion.div
                  animate={{ scale: [1, 1.2, 1] }}
                  transition={{
                    duration: 0.5,
                    repeat: Infinity,
                    ease: "easeInOut",
                  }}
                >
                  <span role="img" aria-label="siren">
                    🚨
                  </span>
                </motion.div>
                Urgent
              </div>
            </div>
          ) : isFastTrack ? (
            <div className="absolute flex md:flex-row items-end md:gap-2 gap-1 top-2 right-3 text-white text-xs font-bold rounded-full shadow-lg z-10">
              <motion.div
                animate={{ rotate: [0, 2, -2, 0] }}
                transition={{ duration: 0.5, repeat: Number.POSITIVE_INFINITY }}
              >
                <div className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium bg-yellow-400 text-black">
                  <Crown className="h-3 w-3" />
                  VIP
                </div>
              </motion.div>
            </div>
          ) : null}

          {/* Golden or Red Line Animation */}
          {isOverdue ? (
            <div className="absolute bottom-0 left-0 right-0 h-1 rounded-b-xl overflow-hidden rounded-b-lg">
              <motion.div
                animate={{ x: ["-100%", "100%"] }}
                transition={{
                  duration: 1.5,
                  repeat: Number.POSITIVE_INFINITY,
                  ease: "linear",
                }}
                className="h-full w-full bg-gradient-to-r from-transparent via-red-500 to-transparent"
              />
            </div>
          ) : isFastTrack ? (
            <div className="absolute bottom-0 left-0 right-0 h-1 rounded-b-xl overflow-hidden rounded-b-lg">
              <motion.div
                animate={{ x: ["-100%", "100%"] }}
                transition={{
                  duration: 1.5,
                  repeat: Number.POSITIVE_INFINITY,
                  ease: "linear",
                }}
                className="h-full w-full bg-gradient-to-r from-transparent via-yellow-500 to-transparent"
              />
            </div>
          ) : null}

          <div className="flex items-center md:gap-3 gap-1 md:flex-row flex-col">
            <div className="w-full flex items-center h-full gap-3">
              <div className="flex items-center justify-center flex-shrink-0 min-w-[24px]">
                {shouldAnimate ? (
                  <motion.div
                    animate={{
                      scale: [1, 1.2, 1],
                      opacity: [1, 0.8, 1],
                    }}
                    transition={{
                      duration: 0.5,
                      repeat: Number.POSITIVE_INFINITY,
                      ease: "easeInOut",
                    }}
                    className="text-lg font-bold"
                  >
                    🚨
                  </motion.div>
                ) : (
                  <span className="text-lg font-bold text-gray-700">
                    {index + 1}
                  </span>
                )}
              </div>

              <img
                src={
                  item.album?.images?.[0]?.url ||
                  "/placeholder.svg?height=48&width=48"
                }
                alt={item.name}
                className="md:w-20 md:h-20 h-12 w-12 object-cover rounded-xl"
                loading="lazy"
              />

              <div className="mr-2 h-full w-full flex flex-col text-black gap-2">
                <div className="w-full flex items-center justify-between gap-2">
                  <h3
                    className="font-semibold md:text-base text-sm m-0 leading-none 
               truncate block flex-grow md:max-w-[60%] w-full"
                  >
                    {item.name}
                  </h3>
                </div>

                <p className="md:text-sm text-xs text-gray-400 truncate m-0 leading-none">
                  {item.artists?.[0]?.name}
                </p>
              </div>
            </div>

            {/* User info (desktop) */}
            <div className="hidden 2xl:flex flex-col items-center absolute md:left-1/2 md:transform md:-translate-x-1/2 items-center gap-1 text-sm p-4">
              <div className="w-12 h-12 rounded-full overflow-hidden">
                {item.users?.[0]?.avatar ? (
                  <img
                    src={item.users[0].avatar}
                    alt={item.users[0].display_name}
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                ) : (
                  <div className="w-full h-full bg-gray-300 flex items-center justify-center">
                    <User className="w-3 h-3 text-gray-600" />
                  </div>
                )}
              </div>
              <span className="text-gray-500 text-base">
                {item.users?.[0]?.display_name || "Anonymous"}
              </span>
            </div>

            <span className="2xl:flex absolute hidden right-8 bottom-2 text-gray-500">
              {isOverdue
                ? formatTimer(elapsedTime)
                : item.approvedAt
                  ? formatItemTime(item.approvedAt)
                  : formatItemTime(item.updatedAt)}
            </span>

            {!isHistory ? (
              <div className="flex flex-1 gap-2 flex-shrink-0 w-full">
                {type === "request" ? (
                  <>
                    <button
                      onClick={() => handleDecline(item.id)}
                      disabled={!isFirst}
                      className={`w-1/2 px-3 py-1.5 border border-red-300 text-red-600 rounded-lg text-sm font-medium flex items-center justify-center gap-1 ${
                        !isFirst ? "opacity-50 cursor-not-allowed" : ""
                      }`}
                    >
                      <X className="w-4 h-4" /> Decline
                    </button>
                    <button
                      onClick={() => handleAccept(item.id)}
                      disabled={!isFirst}
                      className={`w-1/2 justify-center px-3 py-1.5 bg-logo text-white rounded-lg text-sm font-medium flex items-center gap-1 ${
                        !isFirst ? "opacity-50 cursor-not-allowed" : ""
                      }`}
                    >
                      <Check className="w-4 h-4" /> Accept
                    </button>
                  </>
                ) : type === "approved" ? (
                  <>
                    <button
                      onClick={handleNotYet}
                      className={`w-1/2 min-w-28 flex items-center justify-center gap-2 px-2 py-1.5 bg-slate-400 text-white rounded-lg text-sm font-medium `}
                    >
                      <X className="w-4 h-4" /> Not Yet
                    </button>
                    <button
                      onClick={() => handlePlayed(item.id)}
                      className={`w-1/2 flex items-center justify-center gap-2 px-4 py-1.5 rounded-lg text-sm font-medium ${
                        isOverdue
                          ? "bg-red-500 text-white shadow-lg shadow-red-500"
                          : isFastTrack
                            ? "bg-yellow-400 text-white shadow-lg shadow-yellow-400"
                            : "bg-logo text-white"
                      }`}
                    >
                      <Check className="w-4 h-4" /> Played
                    </button>
                  </>
                ) : (
                  <span className="px-3 py-1.5 bg-green-100 text-green-700 rounded-lg text-sm font-medium">
                    Played
                  </span>
                )}
              </div>
            ) : (
              <div
                className={`block absolute top-3 right-4 py-1 text-white rounded-md px-2 md:text-base text-xs ${
                  item.status === "played" ? "bg-green-600" : "bg-red-600"
                }`}
              >
                {item.status === "played" ? "Played" : "Rejected"}
              </div>
            )}
          </div>

          {/* Mobile user info */}
          <div className="flex items-center justify-between text-sm 2xl:hidden mt-2">
            <div className="flex flex-row items-center gap-2">
              <span className="text-gray-500">By-</span>
              <div className="flex sm:flex-row flex-row-reverse gap-2">
                <span className="text-gray-700 font-medium">
                  {item.users?.[0]?.display_name || "Anonymous"}
                </span>
                <div className="w-5 h-5 rounded-full overflow-hidden">
                  {item.users?.[0]?.avatar ? (
                    <img
                      src={item.users[0].avatar}
                      alt={item.users[0].display_name}
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                  ) : (
                    <div className="w-full h-full bg-gray-300 flex items-center justify-center">
                      <User className="w-3 h-3 text-gray-600" />
                    </div>
                  )}
                </div>
              </div>
            </div>
            <span className="text-gray-500">
              {isOverdue
                ? formatTimer(elapsedTime)
                : item.approvedAt
                  ? formatItemTime(item.approvedAt)
                  : formatItemTime(item.updatedAt)}
            </span>
          </div>
        </div>
      </div>
    );
  },
);

export const EnhancedShoutCard = memo(
  ({ shout, index, type = "request", handleNotYet, ShoutoutPlayed }) => (
    <div className="w-full">
      <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-200/50 relative">
        {/* Played Tag in Top Right for History */}
        {type !== "request" && (
          <span className="absolute right-3 top-3 px-3 py-1.5 bg-green-100 text-green-700 rounded-lg text-sm font-medium shadow-sm">
            Played
          </span>
        )}

        <div className="flex flex-row gap-3 mb-3 w-full">
          <div className="flex-1 relative min-w-0 mr-2 flex flex-col w-full sm:flex-row gap-3 sm:items-start items-start justify-between">
            <div className="flex-1 min-w-0 w-full">
              <h2 className="font-semibold text-red-600 ml-2 md:text-xl text-base leading-tight mb-1">
                {shout.shoutOut}
              </h2>

              {shout.additionalInfo && (
                <div className="mb-2 mt-3 w-full px-2 pt-2 pb-1 bg-logo/20 border-l-4 border-logo rounded-md shadow-sm">
                  <div className="flex items-center gap-2 mb-3">
                    <span className="text-logo text-2xl">
                      <TbMessageFilled />
                    </span>
                    <span className="text-sm md:text-base font-semibold text-gray-700">
                      Special Message
                    </span>
                  </div>
                  <p className="text-sm text-gray-800">
                    {shout.additionalInfo}
                  </p>
                </div>
              )}
            </div>

            {/* Buttons only for request type */}
            {type === "request" && (
              <div className="sm:absolute right-2 top-0 flex gap-2 flex-shrink-0 sm:mt-0 w-full sm:w-auto justify-start sm:justify-end">
                <button
                  className="w-1/2 sm:w-auto px-3 py-1.5 bg-gray-200 text-gray-600 rounded-lg text-sm font-medium"
                  onClick={handleNotYet}
                >
                  Not Yet
                </button>
                <button
                  className="w-1/2 sm:w-auto px-3 py-1.5 bg-logo text-white rounded-lg text-sm font-medium"
                  onClick={() => ShoutoutPlayed(shout._id)}
                >
                  Played
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex flex-row sm:items-center justify-between gap-2 text-sm mt-2">
          <div className="flex items-center gap-2 text-sm">
            <span className="text-gray-500">By-</span>
            <span className="text-gray-700 font-medium">
              {shout.user?.display_name || "Anonymous"}
            </span>
            <div className="w-5 h-5 rounded-full overflow-hidden">
              {shout.user?.avatar ? (
                <img
                  src={shout.user.avatar}
                  alt={shout.user.display_name}
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              ) : (
                <div className="w-full h-full bg-gray-300 flex items-center justify-center">
                  <User className="w-3 h-3 text-gray-600" />
                </div>
              )}
            </div>
          </div>

          <span className="text-gray-500">
            {new Date(shout.updatedAt).toLocaleTimeString([], {
              hour: "2-digit",
              minute: "2-digit",
              hour12: true,
            })}
          </span>
        </div>
      </div>
    </div>
  ),
);
